import { fetchCheckDeviceConnection } from '@/entities/device';
import {
  fetchPushStartEcgStreaming,
  handleStartStreamECG,
  formatPatientAge,
  convertBinaryToECGData,
  checkBiocoreProDeviceCellularStreamingSchema,
  checkDeviceUsingCellular,
} from '@/entities/study';
import {
  DEVICE,
  DOUBLE_HYPHEN_BULLET,
  EMITTER_CONSTANTS,
  STREAMING_EMITTER_CONSTANTS,
} from '@/shared/config';
import {
  emitter,
  generateFullName,
  SocketIo,
  SocketEcgStreaming as SocketStreaming,
} from '@/shared/lib';
import { useEmitter, useSetState } from '@/shared/lib/customHooks';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { t } from 'ttag';
import { convertBase64ToArrayForEcg } from '../../../lib/viewLiveEcgModal';
import type { ViewLiveEcgModalProps } from './viewLiveEcgModal.types';

/**
 * Drives the live ECG viewer modal: transport selection, streaming lifecycle, inactivity handling and the patient panel. It picks cellular or Bluetooth streaming from the device model and cellular schema checks, starts the stream on mount and stops it on unmount, drains the raw sample buffer into the chart every three seconds, arms a no-data watchdog, pauses after ten idle minutes, stops when the tab stays hidden 30 seconds, subscribes to the streaming emitters and exposes resetPause via the ref.
 *
 * @param open - Whether the modal is open, gating the buffer drain interval.
 * @param study - Study whose device is streamed and whose patient panel is built.
 * @param displayConfirmActivityModal - Callback opening the confirm-activity modal when work pauses.
 * @param ref - Forwarded ref exposing resetPause to the parent.
 * @returns Modal state plus the streaming transport flag, restart and retry handlers, ECG data callbacks, the sample buffer ref and the patient information rows.
 */
export const useViewLiveEcgModal = (
  { open, study, displayConfirmActivityModal }: ViewLiveEcgModalProps,
  ref: React.Ref<any>,
) => {
  const [state, setState] = useSetState<any>({
    bpm: 0,
    ecgObject: {
      dataSamples: [[], [], []],
      speed: 25,
      gain: 655.35875,
      hiddenChannels: [0, 1, 2],
      numChannels: 3,
      sampleRate: 250,
      diagnosticLead: 2,
    },
    configChart: {
      isDisplayScrollbar: false,
    },
    time: 0,
    pause: false,
    isLoadingEcg: true,
    isConnectionError: false,
    isStoppedLiveEcgViaCellular: false,
    isRetrying: false,
    isStreaming: false,
  });

  const ecgStreamingDataBuffer = useRef<any>([]);
  const ecgStreamingRawDataBuffer = useRef<any>([]);
  const bufferInterval = useRef<any>(undefined);
  const firstCalculate = useRef<any>(false);
  const hiddenPage = useRef<any>(false);
  const pausePage = useRef<any>(false);
  const streamingConfig = useRef<any>({
    roomId: '',
    ownerConnectionId: '',
    connectionIds: [],
    token: '',
  });
  const timerRef = useRef<any>(undefined);
  const pauseConnectTimeout = useRef<any>(undefined);
  const dataTimeout = useRef<any>(undefined);
  const hiddenPageTimeout = useRef<any>(undefined);
  const pingpongInterval = useRef<any>(undefined);

  // For customer environment
  /**
   * True when the study's device has to stream live ECG over cellular rather than Bluetooth. Derived from the device model being BIOTRES_PRO combined with checkDeviceUsingCellular and checkBiocoreProDeviceCellularStreamingSchema on that device.
   *
   * @returns Whether cellular streaming applies to this study's device.
   */
  const shouldStreamViaCellular =
    study?.device?.model === DEVICE.BIOTRES_PRO &&
    checkDeviceUsingCellular(study?.device) &&
    checkBiocoreProDeviceCellularStreamingSchema(study?.device);
  // *: For testing (CP-2.25.0)
  // const shouldStreamViaCellular = study.device.model === DEVICE.BIOTRES_PRO;

  // For customer environment
  // const STOP_STREAMING_TIMEOUT = 40000;
  // *: For testing (CP-2.25.0)
  const STOP_STREAMING_TIMEOUT = shouldStreamViaCellular ? 40000 : 70000;

  /**
   * Wipes every trace of the current live ECG session so a new one can start clean. Resets the streaming room and token config plus both sample buffers, restores the default three-channel ecgObject and chart config in state, and clears the pending reconnect, data-timeout and ping-pong timers.
   *
   * @returns Nothing.
   */
  const resetData = () => {
    streamingConfig.current = {
      roomId: '',
      ownerConnectionId: '',
      connectionIds: [],
      token: '',
    };
    ecgStreamingDataBuffer.current = [];
    ecgStreamingRawDataBuffer.current = [];
    setState({
      ecgObject: {
        dataSamples: [[], [], []],
        speed: 25,
        gain: 655.35875,
        hiddenChannels: [0, 1, 2],
        numChannels: 3,
        sampleRate: 250,
        diagnosticLead: 2,
      },
      configChart: {
        isDisplayScrollbar: false,
      },
    });
    if (pauseConnectTimeout.current) {
      clearTimeout(pauseConnectTimeout.current);
      pauseConnectTimeout.current = undefined;
    }
    if (dataTimeout.current) {
      clearTimeout(dataTimeout.current);
      dataTimeout.current = undefined;
    }
    if (pingpongInterval.current) {
      clearInterval(pingpongInterval.current);
      pingpongInterval.current = undefined;
    }
  };

  /**
   * Tears down the live ECG stream and returns the modal to its error state. Closes the streaming socket, leaves the study's liveEcg room, resets bpm, elapsed time, pause, streaming and loading flags with the connection-error flag raised (marking a cellular-side stop when told so), then clears the buffers and timers.
   *
   * @param isStoppedLiveEcgViaCellular - Whether the stream was stopped from the cellular device side.
   * @returns Nothing.
   */
  const handleStopStreaming = (isStoppedLiveEcgViaCellular = false) => {
    SocketStreaming.close();
    (SocketIo as any).leaveRoom(`liveEcg:${study.device.deviceId}`);

    const setStateObject = {
      isConnectionError: true,
      isLoadingEcg: false,
      isRetrying: false,
      isStreaming: false,
      bpm: 0,
      pause: false,
      time: 0,
    };
    if (isStoppedLiveEcgViaCellular) {
      Object.assign(setStateObject, { isStoppedLiveEcgViaCellular: true });
    }
    setState(setStateObject);

    resetData();
  };

  /**
   * Arms the watchdog that ends the live ECG stream when samples stop arriving. Clears any timeout already pending and schedules a new one that stops streaming after the configured no-data timeout.
   *
   * @returns Nothing.
   */
  const handleDataTimeout = () => {
    // *: Close socket connection when not receive data after 30s
    if (dataTimeout.current) {
      clearTimeout(dataTimeout.current);
      dataTimeout.current = undefined;
    }
    dataTimeout.current = setTimeout(() => {
      handleStopStreaming();
    }, STOP_STREAMING_TIMEOUT);
  };

  /**
   * Starts live ECG streaming for a device that relays through the patient's phone over Bluetooth. Pushes the start-streaming request for the study, device and model and then opens the streaming socket connection, logging the failure to the console when the request throws.
   *
   * @returns Promise that settles once streaming was requested or the error logged.
   */
  const startEcgStreamingBluetooth = async () => {
    try {
      await fetchPushStartEcgStreaming({
        studyId: study.id,
        deviceId: study.device.deviceId,
        model: study.device.model,
      });
      SocketStreaming.connectToServer();
    } catch (error) {
      console.error('Failed to start ECG streaming: ', error);
    }
  };

  /**
   * Starts live ECG streaming for a device that streams directly over cellular. Sends the start-stream request for the study and device and then opens the streaming socket connection, logging the failure to the console when the request throws.
   *
   * @returns Promise that settles once streaming was requested or the error logged.
   */
  const startEcgStreamingCellular = async () => {
    try {
      await handleStartStreamECG({
        studyId: study.id,
        deviceId: study.device.deviceId,
      });
      SocketStreaming.connectToServer();
    } catch (error) {
      console.error('Failed to start stream ECG: ', error);
    }
  };

  /**
   * Asks the device whether it is reachable before Bluetooth ECG streaming begins. Sends the check-connection request for the study, device and model, and on failure logs it and puts the modal into the connection-error state with loading and retrying cleared.
   *
   * @returns Promise that settles once the check has completed or the error state is set.
   */
  const checkDeviceConnecion = async () => {
    try {
      await fetchCheckDeviceConnection({
        studyId: study.id,
        deviceId: study.device.deviceId,
        model: study.device.model,
      });
    } catch (error) {
      console.error('Failed to check device connection: ', error);
      setState({
        isConnectionError: true,
        isLoadingEcg: false,
        isRetrying: false,
      });
    }
  };

  /**
   * Retries the live ECG connection after a failed attempt. Raises the retrying flag, re-emits the study's liveEcg room, re-arms the no-data watchdog and then either restarts cellular streaming or re-checks the device connection depending on the transport, logging any failure to the console.
   *
   * @returns Nothing.
   */
  const handleRetryClick = () => {
    try {
      setState({ isRetrying: true });
      (SocketIo as any).emitRoom(`liveEcg:${study.device.deviceId}`);
      handleDataTimeout();
      if (shouldStreamViaCellular) {
        startEcgStreamingCellular();
      } else {
        checkDeviceConnecion();
      }
    } catch (error) {
      console.error('Failed to retry to check device connection: ', error);
    }
  };

  /**
   * Restarts a cellular live ECG stream the patient's device had stopped. Puts the modal back into loading with the connection-error and cellular-stopped flags cleared, re-emits the study's liveEcg room, re-arms the no-data watchdog and requests cellular streaming again, logging any failure to the console.
   *
   * @returns Nothing.
   */
  const restartEcgStreaming = () => {
    try {
      setState({
        isLoadingEcg: true,
        isConnectionError: false,
        isStoppedLiveEcgViaCellular: false,
      });
      (SocketIo as any).emitRoom(`liveEcg:${study.device.deviceId}`);
      handleDataTimeout();
      startEcgStreamingCellular();
    } catch (error) {
      console.error('Failed to restart ECG streaming: ', error);
    }
  };

  /**
   * Suspends the live ECG stream while the browser tab is hidden. Records document.hidden on the ref and, when hidden, schedules a stop of the stream after 30 seconds, cancelling that timeout when the tab becomes visible again within the window.
   *
   * @returns Nothing.
   */
  const handleHiddenPage = () => {
    hiddenPage.current = document.hidden;
    if (document.hidden) {
      // *: When page is hidden, draw ecg is pending
      // *: Close connect socket after 30s pausing working
      hiddenPageTimeout.current = setTimeout(() => {
        hiddenPageTimeout.current = undefined;
        handleStopStreaming();
      }, 30000);
    } else if (hiddenPageTimeout.current) {
      // *: Continute working before 30s
      clearTimeout(hiddenPageTimeout.current);
      hiddenPageTimeout.current = undefined;
    }
  };

  /**
   * Publishes a new heart-rate reading into the live ECG modal state. Writes the incoming bpm value straight into state so the readout re-renders.
   *
   * @param bpm - Heart rate reported by the ECG calculation.
   * @returns Nothing.
   */
  const onBpmChanged = (bpm: any) => {
    setState({ bpm });
  };

  /**
   * Buffers a calculated ECG chunk for the live trace and seeds the first heart rate. Ignores chunks whose first channel is empty, pushes the samples and bpm onto the streaming data buffer, and writes the bpm into state only for the very first calculated chunk.
   *
   * @param calculatedData - Calculated ECG chunk with per-channel data and a bpm value.
   * @returns Nothing.
   */
  const onCalculatedData = (calculatedData: any) => {
    if (calculatedData?.data[0].length === 0) {
      return;
    }

    ecgStreamingDataBuffer.current.push({
      data: calculatedData.data,
      bpm: calculatedData.bpm,
    });
    if (!firstCalculate.current) {
      setState({ bpm: calculatedData.bpm });
      firstCalculate.current = true;
    }
  };

  /**
   * Throttles the calculated-ECG handler so bursts of chunks do not thrash React state. Wraps the buffering handler in a 100 ms lodash debounce memoized once for the component's lifetime.
   *
   * @returns Debounced handler accepting a calculated ECG chunk.
   */
  const debounceOnCalculatedData = useCallback(
    _.debounce((data) => {
      onCalculatedData(data);
    }, 100),
    [],
  );

  /**
   * Prepares the live ECG session once the streaming socket is connected. Clears any leftover ping-pong interval, joins the streaming room for the study's device id and arms the no-data watchdog.
   *
   * @returns Nothing.
   */
  const onSocketConnected = () => {
    if (pingpongInterval.current) {
      clearInterval(pingpongInterval.current);
      pingpongInterval.current = undefined;
    }
    SocketStreaming.joinRoom(study.device.deviceId);
    handleDataTimeout();
  };

  /**
   * Consumes an incoming streaming socket message and feeds the raw ECG buffer. When the tab is visible it decodes a base64 binary payload into an Int16Array converted to per-channel ECG data, or converts the separate ch1/ch2/ch3 base64 channels, or stops the stream as cellular-initiated on an isStopped payload, and re-arms the no-data watchdog in every case.
   *
   * @param data - Streaming socket message whose payload carries ECG samples or a stop flag.
   * @returns Nothing.
   */
  const onMessageData = (data: any) => {
    const { payload } = data;
    if (!hiddenPage.current) {
      if (payload.data) {
        const decodedData = Buffer.from(payload.data, 'base64');
        const int16ArrayData = new Int16Array(decodedData.buffer);
        const ecgData = convertBinaryToECGData(payload.channels, int16ArrayData);
        ecgStreamingRawDataBuffer.current.push(ecgData);
      } else if (payload.ch1 && payload.ch2 && payload.ch3) {
        const ch1 = convertBase64ToArrayForEcg(payload.ch1);
        const ch2 = convertBase64ToArrayForEcg(payload.ch2);
        const ch3 = convertBase64ToArrayForEcg(payload.ch3);
        ecgStreamingRawDataBuffer.current.push([ch1, ch2, ch3]);
      } else if (payload.isStopped) {
        handleStopStreaming(true);
      }
    }
    handleDataTimeout();
  };

  /**
   * Keeps the live ECG streaming session alive towards the patient's mobile app. Sends a ping message over the streaming socket using the stored room token and connection ids, tagged with the owner connection id and the study device id.
   *
   * @returns Nothing.
   */
  const sendPingPongToMobile = () => {
    SocketStreaming.sendMessage({
      token: streamingConfig.current.token,
      connectionIds: streamingConfig.current.connectionIds,
      payload: {
        type: 'ping',
        connectionId: streamingConfig.current.ownerConnectionId,
        deviceId: study.device.deviceId,
      },
    });
  };

  /**
   * Tracks the streaming room membership for the live ECG session. Stores the room payload on the config ref and, when no ping-pong interval is running yet and more than one connection is present, sends an immediate ping and then repeats it every 30 seconds.
   *
   * @param data - Room payload with the token, owner connection id and connection ids.
   * @returns Nothing.
   */
  const onRoomChanged = (data: any) => {
    streamingConfig.current = { ...data };

    if (pingpongInterval.current) {
      return;
    }

    if (data?.connectionIds.length > 1) {
      sendPingPongToMobile();
      pingpongInterval.current = setInterval(() => {
        sendPingPongToMobile();
      }, 30000);
    }
  };

  /**
   * Reacts to the pause-working signal during a live ECG session. Records the paused state on the ref and, when pausing, drops any pending reconnect timeout and opens the confirm-activity modal, while resuming within the window just clears that timeout.
   *
   * @param pause - Whether the page entered the paused-working state.
   * @returns Nothing.
   */
  const onPauseWorking = (pause: any) => {
    pausePage.current = pause;
    if (pause) {
      pauseConnectTimeout.current = undefined;
      displayConfirmActivityModal(true);
    } else if (pauseConnectTimeout.current) {
      // *: Continute working before 30s
      clearTimeout(pauseConnectTimeout.current);
      pauseConnectTimeout.current = undefined;
    }
  };

  /**
   * Resets the live ECG inactivity counter after user interaction. Writes time back to zero only while the session is not paused, so a paused session keeps its elapsed count.
   *
   * @returns Nothing.
   */
  const clearTime = () => {
    if (!state.pause) {
      setState({ time: 0 });
    }
  };

  /**
   * Pauses the live ECG session once it has run untouched for the ten-minute limit. When the elapsed time reaches 600 seconds it clears the one-second timer interval, nulls the timer ref and sets pause in state.
   *
   * @returns Nothing.
   */
  const listenEvent = () => {
    if (state.time >= 600) {
      // *: 600 second = 10 mins
      clearInterval(timerRef.current);
      timerRef.current = null;
      setState({ pause: true });
    }
  };

  /**
   * Builds the patient rows shown beside the live ECG trace. Returns an empty list for empty input, otherwise reads the patient from data.patient or data.info.patient, falls back to generateFullName for the name, computes the age in whole years with dayjs and emits the patient, gender (defaulted to the placeholder) and formatted DOB entries.
   *
   * @param data - Study object holding the patient either directly or under info.
   * @returns Title and content pairs for the patient information panel.
   */
  const getPatientInformation = (data: any) => {
    if (_.isEmpty(data)) {
      return [];
    }

    const patient = data.patient || data.info?.patient;
    const patientName = patient.name || generateFullName(patient);
    const patientDob = patient.dob;
    const patientAge = dayjs().diff(patientDob, 'years');
    return [
      {
        title: t`Patient`,
        content: patientName,
      },
      {
        title: t`Gender`,
        content: patient.gender ?? DOUBLE_HYPHEN_BULLET,
      },
      {
        title: t`DOB`,
        content: `${patientDob} (${formatPatientAge(patientAge)} yo)`,
      },
    ];
  };

  useImperativeHandle(ref, () => ({
    resetPause: () => {
      setState({
        pause: false,
        time: 0,
      });
    },
  }));

  useEffect(() => {
    (SocketIo as any).emitRoom(`liveEcg:${study.device.deviceId}`);
    handleDataTimeout();
    if (shouldStreamViaCellular) {
      startEcgStreamingCellular();
    } else {
      checkDeviceConnecion();
    }
    return () => {
      handleStopStreaming();
    };
  }, []);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleHiddenPage);
    return () => {
      document.removeEventListener('visibilitychange', handleHiddenPage);
    };
  }, []);

  useEffect(() => {
    if (open) {
      bufferInterval.current = setInterval(() => {
        if (ecgStreamingRawDataBuffer.current.length > 0) {
          setState({
            isLoadingEcg: false,
            isRetrying: false,
            isConnectionError: false,
            isStreaming: true,
            ecgObject: {
              ...state.ecgObject,
              dataSamples: ecgStreamingRawDataBuffer.current[0],
            },
          });
          ecgStreamingRawDataBuffer.current.shift();
        }
      }, 3000);
    }
    return () => {
      if (bufferInterval.current) {
        clearInterval(bufferInterval.current);
      }
      if (pauseConnectTimeout.current) {
        clearTimeout(pauseConnectTimeout.current);
      }
      if (dataTimeout.current) {
        clearTimeout(dataTimeout.current);
      }
      if (pingpongInterval.current) {
        clearInterval(pingpongInterval.current);
      }
    };
  }, [open]);

  useEffect(() => {
    if (state.isStreaming) {
      if (!state.pause) {
        timerRef.current = setInterval(() => {
          setState((prevState) => ({
            time: prevState.time + 1,
          }));
        }, 1000);
      }
      return () => {
        clearInterval(timerRef.current);
      };
    }
    return () => {};
  }, [state.pause, state.isStreaming]);

  useEffect(() => {
    listenEvent();
  }, [state.time]);

  useEffect(() => {
    document.addEventListener('mousemove', clearTime);
    document.addEventListener('mousedown', clearTime);
    document.addEventListener('keydown', clearTime);
    document.addEventListener('scroll', clearTime);

    emitter.emit(STREAMING_EMITTER_CONSTANTS.PAUSE_WORKING_STATUS, state.pause);

    return () => {
      document.removeEventListener('mousemove', clearTime);
      document.removeEventListener('mousedown', clearTime);
      document.removeEventListener('keydown', clearTime);
      document.removeEventListener('scroll', clearTime);
    };
  }, [state.pause]);

  useEmitter(
    EMITTER_CONSTANTS.SEND_DEVICE_CONNECTION_STATUS,
    (msg: any) => {
      if (msg.isConnecting) {
        if (state.isStreaming) {
          return;
        }

        startEcgStreamingBluetooth();
      } else {
        handleStopStreaming();
      }
    },
    [state.isStreaming],
  );

  useEmitter(STREAMING_EMITTER_CONSTANTS.WEBSOCKET_CONNECTED, onSocketConnected, []);

  useEmitter(STREAMING_EMITTER_CONSTANTS.ON_MESSAGE_DATA, onMessageData, []);

  useEmitter(STREAMING_EMITTER_CONSTANTS.ON_ROOM_CHANGED, onRoomChanged, []);

  useEmitter(STREAMING_EMITTER_CONSTANTS.PAUSE_WORKING_STATUS, onPauseWorking, []);

  const patientInfo = getPatientInformation(study);

  return {
    state,
    shouldStreamViaCellular,
    restartEcgStreaming,
    handleRetryClick,
    debounceOnCalculatedData,
    onBpmChanged,
    ecgStreamingDataBuffer,
    patientInfo,
  };
};
